import { formatPriceDZD } from '@/utilities/PriceFormat'
import {
  BedSingle,
  Camera,
  ChevronLeft,
  ChevronRight,
  SquareArrowOutUpRight,
  Video,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import Link from 'next/link'
import AlgerianWilaya from '@/utilities/data/Algeria_Willaya.json'
import AlgeriaCities from '@/utilities/data/Algeria_Cities.json'
import MuxPlayer from '@mux/mux-player-react'
import { cn } from '@/utilities/ui'
import { useTheme } from '@/providers/Theme'
import Slider from 'react-slick'
import { ImageMedia } from '../Media/ImageMedia'

const NextArrow = ({ onClick }: { onClick?: () => void }) => (
  <div
    onClick={(e) => {
      e.stopPropagation()
      e.preventDefault()
      onClick?.(e)
    }}
    className="absolute top-1/2 right-2 -translate-y-1/2 z-20 bg-white/70 hover:bg-white p-1 rounded-full shadow cursor-pointer"
  >
    <ChevronRight size={18} />
  </div>
)

const PrevArrow = ({ onClick }: { onClick?: () => void }) => (
  <div
    onClick={(e) => {
      e.stopPropagation()
      e.preventDefault()
      onClick?.(e)
    }}
    className="absolute top-1/2 left-2 -translate-y-1/2 z-20 bg-white/70 hover:bg-white p-1 rounded-full shadow cursor-pointer"
  >
    <ChevronLeft size={18} />
  </div>
)

export const PropertyCard = ({ property, dictionary, lang }) => {
  const { theme } = useTheme()

  const mediaItems = [
    ...(property?.mediaSection?.images || []).map((img) => ({
      type: 'image',
      url: img?.image?.url,
      image: img?.image,
    })),
    ...(property?.mediaSection?.videos || []).map((vid) => ({
      type: 'video',
      playbackId: vid?.video?.mux_playback_id,
    })),
  ]

  const sliderSettings = {
    dots: true,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    cssEase: 'ease-out',
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    appendDots: (dots: React.ReactNode) => (
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ bottom: '20px' }}
        className="absolute w-full flex justify-center z-20"
      >
        <ul className="flex justify-center">{dots}</ul>
      </div>
    ),
    customPaging: () => <div className="w-2 h-2 bg-white/70 rounded-full hover:bg-white" />,
  }

  const wilayaJson = AlgerianWilaya.find((w) => w.id === property.wilaya)
  const cityJson = AlgeriaCities.find((c) => c.id === property.city)

  const wilayaLabel = lang === 'ar' ? wilayaJson?.ar_name || '' : wilayaJson?.name || ''
  const cityLabel = lang === 'ar' ? cityJson?.ar_name || '' : cityJson?.name || ''

  // Format the location
  const location =
    property.wilaya && property.city
      ? `${cityLabel}, ${wilayaLabel}`
      : property.wilaya ||
        property.locationSection?.neighborhood ||
        dictionary?.propertyDetail?.homePageListing?.locationNotSpecified ||
        'Location not specified'

  // Get the property's size from the surface field
  const size = property.surface
    ? `${property.surface} m²`
    : property.detailSection?.surface
      ? `${property.detailSection.surface} m²`
      : '—'

  // Get the property's bedrooms from the pieces field
  const bedrooms = property.rooms || property.detailSection?.rooms || '—'

  // Get the property's afterPriceLabel
  const afterPrice = property.afterPriceLabel || property.propertyDescription?.afterPriceLabel

  const handleClickPropertyLink = (e: React.SyntheticEvent) => {
    e.stopPropagation()
    e.preventDefault()

    if (lang) {
      window.location.href = `/${lang}/properties/${property.slug}`
    } else {
      window.location.href = `/properties/${property.slug}`
    }
  }

  return (
    <Link href={lang ? `/${lang}/properties/${property.slug}` : `/properties/${property.slug}`}>
      <div className="rounded-lg overflow-hidden bg-card border">
        {/* Property Image */}
        <div className="relative">
          <div className="aspect-[4/3] w-full relative">
            {mediaItems.length > 0 ? (
              mediaItems.length === 1 ? (
                mediaItems[0]?.type === 'image' ? (
                  <ImageMedia
                    resource={mediaItems[0].image}
                    imgClassName="object-cover"
                    alt={property?.title || 'Property media'}
                    loading="eager"
                    priority
                    fill
                    size="(max-width: 768px) 100vw, 50vw"
                  />
                ) : (
                  <MuxPlayer
                    playbackId={mediaItems[0].playbackId}
                    accentColor="#006633"
                    className="object-cover w-full h-full"
                    playsInline
                    muted
                  />
                )
              ) : (
                <Slider {...sliderSettings} className="h-full">
                  {mediaItems.slice(0, 6).map((item, index) =>
                    item.type === 'image' ? (
                      <div key={index} className="relative w-full overflow-hidden aspect-[4/3]">
                        <ImageMedia
                          resource={item.image}
                          imgClassName="object-cover"
                          fill
                          size="(max-width: 768px) 100vw, 50vw"
                          alt={property?.title + '-' + index || 'Property image'}
                          priority={index < 3}
                          loading={index < 3 ? 'eager' : 'lazy'}
                        />
                      </div>
                    ) : (
                      <div
                        key={index}
                        className="h-64 w-full relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MuxPlayer
                          playbackId={item.playbackId}
                          accentColor="#006633"
                          className="object-cover w-full h-full"
                          playsInline
                          muted
                        />
                      </div>
                    ),
                  )}
                </Slider>
              )
            ) : (
              <div className="h-64 w-full bg-gray-200 flex items-center justify-center">
                <span className="text-gray-500 text-sm">No media available</span>
              </div>
            )}
          </div>

          {/* Price Badge */}
          <div className="absolute top-3 left-3 bg-white py-1 px-3 rounded-md font-semibold text-sm">
            {formatPriceDZD(property.propertyDescription.price, lang)} {afterPrice && ` / ${afterPrice}`}
          </div>
        </div>

        {/* Property Info */}
        <div className="p-4">
          <div className="w-full flex justify-between border-b border-b-border pb-2">
            <div className="flex flex-col gap-2 overflow-hidden">
              <h3 className="text-typography text-base font-medium truncate max-w-xs">
                {property.title}
              </h3>
              <p className="text-sm text-muted mb-2">{location}</p>
            </div>

            {/* Features */}
            <div className="flex flex-col gap-2">
              {property?.propertyDescription?.category !== 'land' && (
                <div className="text-typography flex items-center gap-2">
                  <span className="inline-block">
                    <BedSingle size={16} />
                  </span>
                  <span>{bedrooms}</span>
                </div>
              )}
              {property?.detailSection?.surface && (
                <div className="text-typography flex items-center gap-2">
                  {theme === 'dark' ? (
                    <img src="/icons/expand-w.svg" alt="w expand" />
                  ) : (
                    <img src="/icons/expand.svg" alt="expand" />
                  )}
                  <span className="text-sm">{size}</span>
                </div>
              )}
            </div>
          </div>

          {/* Views & Link */}
          <div className="flex justify-between items-center mt-4 h-[35px]">
            <div className="flex gap-2">
              <div
                className={cn(
                  'h-full flex items-center text-sm px-2 py-2 rounded-md gap-1',
                  theme === 'dark' ? '!text-white' : '',
                )}
              >
                <span className="ml-1">{property.mediaSection.images.length || 0}</span>
                <Camera size={16} />
              </div>

              {property?.mediaSection?.videos && (
                <div
                  className={cn(
                    'h-full flex items-center text-sm px-2 py-2 rounded-md gap-1',
                    theme === 'dark' ? '!text-white' : '',
                  )}
                >
                  <span className="ml-1">{property.mediaSection.videos?.length || 0}</span>
                  <Video size={16} />
                </div>
              )}
            </div>

            <Button
              asChild
              variant={'outline'}
              className={cn(
                '!h-[35px] !w-[35px] text-black bg-transparent transition-colors border !border-border !p-0',
                theme === 'dark' ? '!text-white hover:bg-slate-900' : '',
              )}
            >
              <div onClick={handleClickPropertyLink} className="!w-[14px] !h-[14px]">
                <SquareArrowOutUpRight size={16} />
              </div>
            </Button>
          </div>
        </div>
      </div>
    </Link>
  )
}
